import React from "react";
import { useTheme } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import TenantLogo from "../../components/TenantLogo";
import "./checkin.css";

/**
 * The brand shown in the header: the training center that owns the session when the server told us (signed-out scan),
 * else the signed-in user's center, else the center for this domain, else the generic name. Same logo and name as the
 * app's top menu.
 */
export function resolveBrand(...sources) {
  const found = sources.find((s) => s && s.name);
  return { name: found?.name || "Training Center", logo: found?.logo || found?.icon || null };
}

/** The server stores naive UTC timestamps; mark them as UTC so the browser shows the student's local time. */
export function formatTime(iso) {
  if (!iso) return "";
  const utc = /[Zz]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`;
  return new Date(utc).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function clock(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  return new Date(2000, 0, 1, h, m).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/** "Mon, Oct 5 · 9:00 AM – 12:00 PM" from the session summary; empty when the session has no date. */
export function whenLabel(session) {
  if (!session?.date) return "";
  const [y, mo, d] = session.date.split("-").map(Number);
  const day = new Date(y, mo - 1, d).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
  const start = clock(session.startTime);
  const end = clock(session.endTime);
  const range = start && end ? `${start} – ${end}` : start;
  return range ? `${day} · ${range}` : day;
}

export function initials(name) {
  return (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
}

export function CheckinAlert({ children, warn }) {
  return (
    <div className={`ck-alert${warn ? " ck-alert--warn" : ""}`} role={warn ? "status" : "alert"}>
      <ErrorOutlineIcon sx={{ fontSize: 18 }} />
      <span>{children}</span>
    </div>
  );
}

/** The session being checked in to: name, when, where. Renders only the lines the server gave us. */
export function SessionCard({ session }) {
  if (!session?.name) return null;
  const when = whenLabel(session);
  return (
    <div className="ck-sess">
      <div className="ck-sess__top">
        <span className="ck-sess__nm">{session.name}</span>
      </div>
      {(when || session.location) && (
        <div className="ck-meta">
          {when && <div><CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />{when}</div>}
          {session.location && <div><PlaceOutlinedIcon sx={{ fontSize: 16 }} />{session.location}</div>}
        </div>
      )}
    </div>
  );
}

/** Phone-first page frame with the tenant-branded header. `onBack` adds a back button. */
export default function CheckinShell({ brand, onBack, children }) {
  const theme = useTheme();
  return (
    <div className="ck-root">
      <div className="ck-col">
        <header className="ck-hdr">
          {onBack && (
            <button type="button" className="ck-back" aria-label="Back" onClick={onBack}>
              <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
            </button>
          )}
          <TenantLogo src={brand.logo} name={brand.name} size={32} color={theme.palette.primary.main} />
          <b>{brand.name}</b>
        </header>
        <main className="ck-body">{children}</main>
      </div>
    </div>
  );
}
